import { motion } from "framer-motion";
import { MessageCircle, Calendar, Coffee, ArrowRight } from "lucide-react";
import { useLocation } from "react-router-dom";

/**
 * NextStep — Behavior design (Fogg): reduzir ansiedade da decisão mostrando
 * exatamente o que acontece depois da mensagem. Ancoragem em "conversa" (baixo custo)
 * em vez de "consulta" (alto compromisso). Framing de risco reduzido.
 */
const steps = [
  {
    icon: MessageCircle,
    title: "1. Você manda um oi",
    text: "Pelo WhatsApp, telefone ou formulário. Conte em uma frase o que está acontecendo — sem formalidade.",
  },
  {
    icon: Coffee,
    title: "2. Conversamos sem compromisso",
    text: "Respondemos em horário comercial explicando como funciona, valores, formatos (online ou presencial) e tiramos suas dúvidas.",
  },
  {
    icon: Calendar,
    title: "3. Você decide se quer marcar",
    text: "Se fizer sentido pra você, agendamos a primeira sessão no horário que couber na sua rotina. Sem pressão.",
  },
];

const pageNames: Record<string, string> = {
  "/": "Site",
  "/contato": "Contato",
  "/psicoterapia-adulto": "Psicoterapia Adulto",
  "/psicoterapia-infantil": "Psicoterapia Infantil",
  "/psicoterapia-adolescente": "Psicoterapia Adolescente",
  "/psicoterapia-melhor-idade": "Psicoterapia Melhor Idade",
  "/psicoterapia-casal": "Psicoterapia de Casal",
  "/psicoterapia-breve": "Psicoterapia Breve",
  "/psicoterapia-tcc": "Psicoterapia TCC",
  "/orientacao-vocacional": "Orientação Vocacional",
  "/reorientacao-carreira": "Reorientação de Carreira",
  "/avaliacao-psicodiagnostico": "Psicodiagnóstico",
  "/avaliacao-neuropsicologica": "Avaliação Neuropsicológica",
  "/avaliacao-laudo-cirurgia": "Laudo para Cirurgia",
  "/pericia-psicologica": "Perícia Psicológica",
};

const NextStep = () => {
  const location = useLocation();
  const pageName = pageNames[location.pathname] || "Site";
  const wa = `https://wa.me/5511976974905?text=${encodeURIComponent(
    `Oi! Vim pelo site da Equilybra (${pageName}) e gostaria de saber como funciona.`
  )}`;

  return (
    <section aria-labelledby="proximo-passo" className="py-16 md:py-20 bg-gradient-to-b from-background to-secondary/40">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="text-center mb-10">
          <span className="inline-block px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold tracking-wide uppercase mb-3">
            Próximo passo
          </span>
          <h2 id="proximo-passo" className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3">
            O que acontece quando você entra em contato
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Nenhuma cobrança para tirar dúvidas. Você conversa com a gente antes de decidir qualquer coisa.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 mb-10">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="bg-card border border-border rounded-2xl p-6 shadow-soft h-full"
              >
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.text}</p>
              </motion.article>
            );
          })}
        </div>

        <div className="flex flex-col items-center gap-3">
          <a
            href={wa}
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 px-8 py-4 rounded-2xl font-semibold text-base
              bg-gradient-to-br from-[hsl(142,70%,40%)] to-[hsl(152,60%,35%)] text-primary-foreground
              shadow-[0_8px_32px_-8px_hsl(142,70%,40%,0.4)]
              hover:shadow-[0_12px_40px_-8px_hsl(142,70%,40%,0.55)] transition-shadow"
          >
            <MessageCircle className="w-5 h-5" />
            Mandar um oi pelo WhatsApp
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </a>
          <p className="text-xs text-muted-foreground text-center max-w-md">
            Resposta em horário comercial (Seg–Sex 8h–21h, Sáb 8h–16h). Sem compromisso, sem cobrança pela conversa inicial.
          </p>
        </div>
      </div>
    </section>
  );
};

export default NextStep;
